import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import * as numbers from "common/numbers";
import Component from "components/Component";
import * as labels from "config.labels";
import { modelName } from "domain/models";
import { useSelector } from "react-redux";
import { useModels } from "store/hooks/modelHooks";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import { useTargetLossRatios } from "store/hooks/targetLossRatioHooks";
import { selectDisplayedTargetLossRatios } from "store/selectors/targetLossRatioSelectors";

const formatRatio = numbers.percentage(2);

const useLabels = () => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  return {
    title: translateLabel(labels.TARGET_LOSS_RATIOS_TITLE, {
      default: "Target Loss Ratios",
    }),
    model: translateLabel(labels.TARGET_LOSS_RATIOS_MODEL, {
      default: "Model",
    }),
    layer: translateLabel(labels.TARGET_LOSS_RATIOS_LAYER, {
      default: "Layer",
    }),
    lossRatio: translateLabel(labels.TARGET_LOSS_RATIOS_LOSS_RATIO, {
      default: "Loss Ratio",
    }),
    empty: translateLabel(labels.TARGET_LOSS_RATIOS_EMPTY, {
      default: "No target loss ratios configured",
    }),
  };
};

export const selectModelKey = (state) => state?.input?.program?.model ?? null;

/*
 * Read-only table of target loss ratios, one row per model and layer:
 * Model | Layer | Loss Ratio. `model` is the selected Model key; when it has
 * rows only those are shown, otherwise the whole schedule. The model name
 * comes from `models` (classifications.models, see useModels). Pure
 * presentation: no store access.
 */
export const TargetLossRatiosTable = ({ rows, model, models }) => {
  const text = useLabels();
  const shownRows = selectDisplayedTargetLossRatios(rows, model);

  if (shownRows.length === 0) {
    return (
      <Typography
        variant={"body2"}
        color={"text.secondary"}
        data-testid={"target-loss-ratios-empty"}
      >
        {text.empty}
      </Typography>
    );
  }

  return (
    <Table size={"small"} aria-label={text.title}>
      <TableHead>
        <TableRow>
          <TableCell>{text.model}</TableCell>
          <TableCell>{text.layer}</TableCell>
          <TableCell align={"right"}>{text.lossRatio}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {shownRows.map((row, index) => (
          <TableRow
            key={`${row.model ?? ""}-${row.layer ?? ""}-${index}`}
            data-testid={"target-loss-ratios-row"}
          >
            <TableCell>{modelName(row.model, models)}</TableCell>
            <TableCell>{row.layer ?? ""}</TableCell>
            <TableCell align={"right"}>{formatRatio(row.lossRatio)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/*
 * Connected card. Any of `rows`, `model`, `models` or `title` may be supplied
 * by the caller (for example the Summary page); whatever is not supplied falls
 * back to the `target_loss_ratios` config, the submission's model and the
 * configured model list.
 */
const TargetLossRatios = ({
  rows,
  model,
  models,
  title,
  ...componentProps
}) => {
  const text = useLabels();
  const fromConfig = useTargetLossRatios();
  const configuredModels = useModels();
  const storeModel = useSelector(selectModelKey);

  return (
    <Component
      title={title ?? text.title}
      loading={
        (rows == null && fromConfig.isLoading) ||
        (models == null && configuredModels.isLoading)
      }
      md_width={12}
      lg_width={6}
      {...componentProps}
    >
      <TargetLossRatiosTable
        rows={rows ?? fromConfig.rows}
        model={model === undefined ? storeModel : model}
        models={models ?? configuredModels.models}
      />
    </Component>
  );
};

export default TargetLossRatios;
